import { ReactNode, useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { PersonAvatar } from '@factorialco/factorial-one-react/dist/experimental'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useMeetingTranscript } from 'modules/meetings/lib/hooks/useMeetingTranscript'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

interface TranscriptMessagesProps {
  children?: ReactNode
  autoScroll?: boolean
}

export const TranscriptMessages = ({ children, autoScroll = false }: TranscriptMessagesProps) => {
  const { meeting } = useMeetingDetails()
  const { transcript } = useMeetingTranscript()
  const attendees = meeting?.attendees || []
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)

  const {
    access: { locale },
  } = useCurrentSession()

  useEffect(() => {
    if (autoScroll && !isHovering && messagesContainerRef.current && transcript?.length) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [transcript, isHovering, autoScroll])

  return (
    <Box
      ref={messagesContainerRef}
      flexDirection="column"
      style={{
        width: '100%',
      }}
      height="full"
      className={styles.hiddenScrollBar}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      padding="s24"
      gap="s24"
    >
      {children ?? children}

      {transcript?.map((utterance, index) => {
        const attendee = attendees?.find(({ id }) => Number(id) === Number(utterance.attendeeId))
        const fullName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
        const firstName = attendee?.employee?.firstName || i18n.t('meetings.unknown_participant')
        const lastName = attendee?.employee?.lastName || ''
        const avatar = attendee?.employee?.avatar?.url || ''
        const timestamp = moment(utterance.timestamp, 'x').locale(locale).format('LT')
        return (
          <Box key={index} flexDirection="column" gap="s8">
            <Box flexDirection="row" gap="s8" alignItems="center">
              <PersonAvatar
                firstName={firstName}
                lastName={lastName}
                size="small"
                src={buildUrl(avatar)}
              />
              <Text.Normal size="200" color="grey1000" weight="semibold">
                {fullName}
              </Text.Normal>
              <Text.Normal size="200" weight="normal">
                •
              </Text.Normal>
              <Text.Normal size="100" color="grey600">
                {timestamp}
              </Text.Normal>
            </Box>
            <Text.Normal size="200" color="grey800">
              {utterance.utterance}
            </Text.Normal>
          </Box>
        )
      })}

      {(!transcript || transcript?.length === 0) && (
        <Text.Normal size="200" color="grey800">
          {i18n.t('meetings.room.transcript.no_transcript_available')}
        </Text.Normal>
      )}
    </Box>
  )
}
